import { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { NavLink, Outlet, useNavigate, useParams } from "react-router";

import { StackApi } from "@/api/stackApi";
import { HELP_MOBILE_USERS } from "@/constants/HelpUrls";
import Avatar from "@/design/components/Avatar";
import PageTitle from "@/design/components/PageTitle";
import { disableSearch, enableSearch, setHelpUrl } from "@/store/reducers/ui";
import useSearch from "@/utils/hooks/useSearch";
import { viSmartphone } from "@sippy-platform/valkyrie";

import { List, ListItem, ListItemAvatar, ListItemButton, ListItemText, useMediaQuery, useTheme } from "@mui/material";
import { keepPreviousData, useQuery } from "@tanstack/react-query";

import { MainDetail } from "./components/MainDetail";
import { MetaStates } from "./components/MetaStates";

export default function MobileSippeersLayout() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const params = useParams();
  const theme = useTheme();
  const matchesMdUp = useMediaQuery(theme.breakpoints.up("md"));

  const searchValue = useSelector((state) => state.ui.searchValue);

  useEffect(() => {
    dispatch(setHelpUrl(HELP_MOBILE_USERS));
  }, [dispatch]);

  // Search every time userList or searchQuery changes
  const getAllMobileSippeers = useQuery({
    queryKey: [params.pbxId, "mobileSippeers"],
    queryFn: () => StackApi.MobileSippeers.getAll({ pbxId: params.pbxId }),
    placeholderData: keepPreviousData,
  });

  // Open first item or new if the viewport is large enough
  const firstNavigationDone = useRef(!!params.mobileSippeerId);
  useEffect(() => {
    if (
      !params.mobileSippeerId &&
      matchesMdUp &&
      getAllMobileSippeers.data?.data?.sippeers.length >= 1 &&
      !firstNavigationDone.current
    ) {
      navigate(`${getAllMobileSippeers.data?.data?.sippeers[0].id}`);
      firstNavigationDone.current = true;
    }
  }, [matchesMdUp, navigate, params.mobileSippeerId, getAllMobileSippeers.data?.data?.sippeers]);

  // Search
  const searchResults = useSearch(
    (getAllMobileSippeers.data?.data?.sippeers ?? [])?.filter((user) => user.exten.startsWith("+")),
    searchValue,
    ["name", "exten"],
  );

  useEffect(() => {
    dispatch(enableSearch());
  }, [dispatch]);
  useEffect(() => () => dispatch(disableSearch()), [dispatch]);

  return (
    <MainDetail.Root id={params.mobileSippeerId}>
      <MainDetail.Main>
        <PageTitle>Mobile users</PageTitle>
        {getAllMobileSippeers.isPending ? (
          <MetaStates.Loading label="Getting your mobile users..." />
        ) : searchResults.length <= 0 ? (
          <MetaStates.NoResults
            icon={viSmartphone}
            totalCount={getAllMobileSippeers.data?.data?.count}
            noItemsLabel="You don't have any mobile users yet"
            noResultsLabel="No mobile users match your search query"
          />
        ) : (
          <List component="nav">
            {searchResults.map((mobileSippeer) => (
              <ListItem disablePadding key={mobileSippeer.id} sx={{ minHeight: 48 }}>
                <ListItemButton
                  component={NavLink}
                  to={`/pbx/${params.pbxId}/call-flow/mobile-users/${mobileSippeer.id}`}
                  selected={mobileSippeer.id == params.mobileSippeerId}
                  sx={{ minHeight: 48 }}
                >
                  <ListItemAvatar>
                    <Avatar text={mobileSippeer.name} />
                  </ListItemAvatar>
                  <ListItemText primary={mobileSippeer.name} secondary={mobileSippeer.exten} />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        )}
      </MainDetail.Main>
      <MainDetail.Detail>
        <Outlet />
      </MainDetail.Detail>
    </MainDetail.Root>
  );
}
